































































































import { isEqual } from 'apollo-utilities';
import Vue from 'vue';
import { Component, Ref, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import FilterBar from '@/components/filter-bar/FilterBar.vue';
import { TitleListGraphql } from '@/lazy-load-components';

import ContentHeader from '@/components/content-header/ContentHeader.vue';
import TitleListOptions from '@/components/list/TitleListOptions.vue';
import LoadSurvey from '@/components/survey/LoadSurvey.vue';
import {
	GetPopularTitlesDocument,
	GetPopularTitlesQuery,
	GetPopularTitlesQueryVariables,
} from '@/pages/graphql/queries/GetPopularTitles.query';

import { CollectionType } from '@/enums/collection-type';
import { TitleListLayout } from '@/enums/title-list-layout';
import { TitleOrder } from '@/enums/title-order';
import { WebLocale } from '@/enums/web-locale';

import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { handleStripeRedirect } from '@/helpers/pro-web-helper';
import { captureMessageForSentry, Sentry } from '@/helpers/sentry-helper';
import { onTitleQuickActionEventHandler } from '@/helpers/title-action-helper';
import TitleHelper, { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import ToastHelper from '@/helpers/toast/toast-helper';
import {
	PageNavigationTransaction,
	sentryPerformanceTracker,
} from '@/helpers/tracking/performance/SentryPerformanceTracker';
import { VideoAdsHelper } from '@/helpers/video-ads-helper';

import { showSponsoredRecommendations } from '@/components/sponsored-recommendations/sponsored-recommendation-config';

import { Content } from '@/interfaces/components/content';
import Provider from '@/interfaces/provider';
import { TitleListName } from '@/interfaces/responses/title-list';

import { FilterCollection } from '@/stores/filter.store';
import { UserInfo, UserSettings } from '@/stores/user.store';

import { MonetizationType, Platform, PopularTitlesSorting, TitleFilter } from '@/@types/graphql-types';

import PopularProviderPageExp from '@/components/experiments/PopularProviderPageExp.vue';
import FilterBarProvider from '@/components/filter-bar/FilterBarProvider.vue';
import type TitleListGraphqlType from '@/components/list/TitleListGraphql.vue';
import { PopularProviderPageExpVariantName } from '@/enums/experiments/popular-provider-page-exp';
import {
	experimentProviders,
	experimentProvidersShortName,
	loadMicrosoftClarity,
	providerPageExpTrackEvent,
} from '@/helpers/experiments/popular-provider-page-exp-helper';
import FilterHelper from '@/helpers/filter-helper';
import { PopularTabType, TitleObjectType } from '@/interfaces/titles';
import { RoutingState } from '@/stores/routing.store';
import BasicButton from '@/ui-components/BasicButton.vue';

const ContentPopularProvider = () => import('@/components/popular/ContentPopularProvider.vue');

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const Routing = namespace('routing');
const SponsoredRecommendations = namespace('sponsoredRecommendations');
const Ui = namespace('ui');
const User = namespace('user');
const Experiment = namespace('experiment');

@Component({
	name: 'Popular',
	components: {
		ContentHeader,
		FilterBar,
		LoadSurvey,
		TitleListGraphql,
		TitleListOptions,
		ContentPopularProvider,
		FilterBarProvider,
		BasicButton,
	},
	apollo: {
		popularPageData: {
			query: GetPopularTitlesDocument,
			fetchPolicy: 'cache-and-network',
			variables(): GetPopularTitlesQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				this.isFirstPageLoaded = true;
				return data.popularTitles;
			},
			error(e) {
				console.log(e);
				process.client ? this.showErrorToast() : null;
			},
		},
	},
})
export default class Popular extends Vue {
	showFooter = false;
	isFirstPageLoaded = false; // SPA loads footer first before titles list content has been rendered -> CLS
	popularPageData: GetPopularTitlesQuery['popularTitles'] = {} as GetPopularTitlesQuery['popularTitles'];
	listLayout: TitleListLayout = TitleListLayout.GRID;
	CollectionType = CollectionType;
	isMounted = false;

	@Constant.Getter providers: () => Provider[];

	@Filter.Getter(CollectionType.POPULAR) filterPopular: FilterCollection;

	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;
	@Language.Getter country: string;

	@Routing.Getter activeCollectionType: CollectionType;
	@Routing.Getter queryParams: any;
	@Routing.State activeRoute: RoutingState['activeRoute'];
	@Routing.State lastRoute: RoutingState['lastRoute'];

	@SponsoredRecommendations.Getter isSponsoredRecommendationCohort: boolean;

	@Ui.State showFilterBar: boolean;

	@User.State settings: UserSettings;
	@User.Getter isHideSeenDislikedTitlesEnabled: boolean;
	@User.Getter isLoggedIn: () => boolean;
	@User.Getter isPremium: boolean;
	@User.Getter userInfo: UserInfo;
	@User.State jwLoginId: string | undefined;

	@Ref('content') readonly content!: Content;
	@Ref('titleList') readonly titleList!: TitleListGraphqlType;

	// POPULAR-PROVIDER-PAGE START
	@Constant.Getter providersByShortName: Record<string, Provider>;
	@Constant.Getter providersById: Record<number, Provider>;

	@Experiment.Getter activeVariantsWithControlGroup: Record<string, string>;

	expShowFilterBar = false;
	isMainCompMounted = false;

	get selectedProvider() {
		const shortName = this.filterPopular.providers[0];
		return this.providersByShortName[shortName];
	}
	get experimentVariant() {
		return this.activeVariantsWithControlGroup[PopularProviderPageExp.id] as PopularProviderPageExpVariantName;
	}

	get isExperimentActivated() {
		return (
			this.isMounted &&
			!this.isLoggedIn() &&
			!!this.experimentVariant &&
			this.filterPopular.providers.length === 1 &&
			experimentProviders.includes(this.filterPopular.providers[0] as experimentProvidersShortName)
		);
	}

	get isVariant_1() {
		return this.isExperimentActivated && this.experimentVariant === PopularProviderPageExpVariantName.VARIANT_1;
	}

	get _showFilterBar() {
		return (!this.isDealsSubscriptionTabSelected && this.isVariant_1 && this.expShowFilterBar) || !this.isVariant_1;
	}

	get selectedTab() {
		if (!FilterHelper.isAnyFilterActive(['content_types'], this.filterPopular)) {
			return PopularTabType.ALL;
		} else if (FilterHelper.isFilterActive('content_types', TitleObjectType.MOVIE, this.filterPopular)) {
			return PopularTabType.MOVIE;
		} else if (FilterHelper.isFilterActive('content_types', TitleObjectType.SHOW, this.filterPopular)) {
			return PopularTabType.SHOW;
		} else if (
			FilterHelper.isFilterActive('content_types', TitleObjectType.DEALS_SUBSCRIPTION, this.filterPopular)
		) {
			return PopularTabType.DEALS_SUBSCRIPTION;
		}
	}

	get isDealsSubscriptionTabSelected() {
		return this.selectedTab === PopularTabType.DEALS_SUBSCRIPTION;
	}

	get catalogText() {
		switch (this.selectedTab) {
			case PopularTabType.ALL:
				return this.$t('WEBAPP_PROVIDER_PAGE_CATALOG_TITLE', {
					provider: this.selectedProvider.clear_name,
					number: this.titleAmount,
				});
			case PopularTabType.MOVIE:
				return this.$t('WEBAPP_PROVIDER_PAGE_MOVIE_CATALOG_TITLE', {
					provider: this.selectedProvider.clear_name,
					number: this.titleAmount,
				});
			case PopularTabType.SHOW:
				return this.$t('WEBAPP_PROVIDER_PAGE_TVSHOW_CATALOG_TITLE', {
					provider: this.selectedProvider.clear_name,
					number: this.titleAmount,
				});
			default:
				return '';
		}
	}

	@Watch('activeRoute.name', { immediate: true })
	async onRouteNameChange(name: string, oldName: string) {
		if (name?.startsWith('app.titles.popular.provider') && name !== oldName) {
			setTimeout(() => {
				providerPageExpTrackEvent({ action: 'impression' });
			});
		}
	}

	@Watch('activeRoute.query')
	async onRouteParamsChange(oldValue: Object, newValue: Object) {
		if (this.isExperimentActivated && !isEqual(oldValue, newValue)) {
			const { popoverController } = await import('@/ionic.loader');
			popoverController.dismiss();
		}
	}

	mainCompMounted() {
		this.isMainCompMounted = true;
	}
	toggleExpShowFilterBar() {
		this.expShowFilterBar = !this.expShowFilterBar;
	}
	// POPULAR-PROVIDER-PAGE END
	get userProviders() {
		return this.providers();
	}

	mounted() {
		this.isMounted = true;
		if (this.isExperimentActivated) {
			loadMicrosoftClarity();
		}
		onTitleQuickActionEventHandler(({ type, titleObjectId, isTitleInList }) => {
			switch (type) {
				case TitleListName.DISLIKELIST:
					this.isHideSeenDislikedTitlesEnabled && !isTitleInList
						? this.removeTitleFromGraphqlCache(titleObjectId)
						: this.updateListEntryInGraphqlCache(titleObjectId, 'dislikelistEntry', isTitleInList);
					break;
				case TitleListName.SEENLIST:
					if (this.isHideSeenDislikedTitlesEnabled && !isTitleInList) {
						this.removeTitleFromGraphqlCache(titleObjectId);
					}
					break;
				default:
					break;
			}
		});
		if (this.$route.query['jw-pro']) {
			handleStripeRedirect(this.$route, this.$router, this.jwLoginId);
		}
		if (!this.isLoggedIn()) VideoAdsHelper.loadVideoAd(this.webLocale);
	}

	get queryVariables(): GetPopularTitlesQueryVariables {
		return {
			popularAfterCursor: '',
			popularTitlesFilter: this.graphqlFilters,
			watchNowFilter: {
				packages: this.graphqlFilters.packages,
				monetizationTypes: this.graphqlFilters.monetizationTypes as MonetizationType[],
			},
			popularTitlesSortBy: this.sortBy,
			language: this.language,
			country: this.country,
			sortRandomSeed: this.randomSeed,
			platform: Platform.Web,
		};
	}

	get showSpinner() {
		return !this.isFirstPageLoaded && this.isTitlesLoading;
	}

	get isActiveCollectionType() {
		return this.activeCollectionType === CollectionType.POPULAR;
	}

	get isUserOnboarded() {
		return this.settings.taste_survey_completed;
	}

	get popularTitles() {
		return this.popularPageData?.edges || [];
	}

	get titleAmount() {
		return this.popularPageData.totalCount;
	}

	get isFirstPage() {
		return !this.popularPageData?.pageInfo?.hasPreviousPage;
	}

	get isAtEndOfList() {
		return !this.popularPageData?.pageInfo?.hasNextPage;
	}

	get isTitlesLoading() {
		return this.$apollo.loading;
	}

	get graphqlFilters(): TitleFilter {
		const selectedProviders =
			this.filterPopular.providers?.length === 0 ? this.settings.providers : this.filterPopular.providers;
		const filterPopular = {
			...this.filterPopular,
			content_types: this.filterPopular.content_types.filter(el => el !== 'deals_subscription'),
		};
		return toGraphqlFilters(filterPopular, selectedProviders, this.isHideSeenDislikedTitlesEnabled);
	}

	get randomSeed() {
		const isRandomSort = this.sortBy === PopularTitlesSorting.Random;

		if (process.server) {
			const randomSeed = Number(this.queryParams.sorting_random_seed) || 1;
			return isRandomSort ? randomSeed : 0;
		}

		const randomSeed = this.filterPopular.sorting_random_seed || 1;
		return isRandomSort ? randomSeed : 0;
	}

	get sortBy(): PopularTitlesSorting {
		// Sort By options from GraphQL are Uppercase, but tracking expects lowercase TODO [Graphql] remove when get rid of REST
		const RestToGraphQLSortingMap: Partial<{ [key in TitleOrder]: PopularTitlesSorting }> = {
			[TitleOrder.POPULARITY]: PopularTitlesSorting.Popular,
			[TitleOrder.TRENDING]: PopularTitlesSorting.Trending,
			[TitleOrder.TMDB_POPULARITY]: PopularTitlesSorting.TmdbPopularity,
			[TitleOrder.ALPHABETICAL]: PopularTitlesSorting.Alphabetical,
			[TitleOrder.RANDOM]: PopularTitlesSorting.Random,
			[TitleOrder.RELEASE_YEAR]: PopularTitlesSorting.ReleaseYear,
			[TitleOrder.RELEVANCE]: PopularTitlesSorting.Relevance,
			[TitleOrder.SCORE_IMDB]: PopularTitlesSorting.ImdbScore,
			[TitleOrder.RUNTIME]: PopularTitlesSorting.Runtime,
		};

		// To fetch titles on server side we need to get sort by option from url params
		if (this.queryParams.sort_by) {
			return RestToGraphQLSortingMap[this.queryParams.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
		}

		if (this.filterPopular.sort_by) {
			return RestToGraphQLSortingMap[this.filterPopular.sort_by as TitleOrder] ?? PopularTitlesSorting.Popular;
		}

		return PopularTitlesSorting.Popular;
	}

	get displaySponsoredRecommendation() {
		return showSponsoredRecommendations(this.country) && this.isSponsoredRecommendationCohort && !this.isPremium;
	}

	popularTitlesFromGraphqlCache() {
		return this.$apollo.getClient().readQuery<GetPopularTitlesQuery, GetPopularTitlesQueryVariables>({
			query: GetPopularTitlesDocument,
			variables: this.queryVariables,
		});
	}

	removeTitleFromGraphqlCache(titleObjectId: number) {
		const titles = this.popularTitlesFromGraphqlCache();

		let newTitles = [];

		if (titles?.popularTitles?.edges?.length) {
			newTitles = titles.popularTitles.edges;

			newTitles = newTitles.filter(item => {
				return item.node.objectId !== titleObjectId;
			});

			this.$apollo.getClient().writeQuery<GetPopularTitlesQuery, GetPopularTitlesQueryVariables>({
				query: GetPopularTitlesDocument,
				variables: this.queryVariables,
				data: {
					popularTitles: {
						...titles.popularTitles,
						totalCount: titles?.popularTitles.totalCount - 1,
						edges: newTitles,
					},
				},
			});
		}
	}

	updateListEntryInGraphqlCache(
		titleObjectId: number,
		listName: 'dislikelistEntry' | 'likelistEntry' | 'watchlistEntry' | 'seenlistEntry',
		isTitleInList: boolean
	) {
		const titles = this.popularTitlesFromGraphqlCache();
		if (titles?.popularTitles?.edges) {
			const updatedTitles = titles.popularTitles.edges.map(title => {
				if (title.node.objectId === titleObjectId) {
					const shouldClearOppositeListEntry =
						!isTitleInList && (listName === 'dislikelistEntry' || listName === 'likelistEntry');
					const oppositeListEntryName =
						listName === 'dislikelistEntry' ? 'likelistEntry' : 'dislikelistEntry';

					return {
						...title,
						node: {
							...title.node,
							...(shouldClearOppositeListEntry ? { [oppositeListEntryName]: null } : {}),
							[listName]: isTitleInList
								? null
								: { createdAt: new Date().toISOString(), __typename: 'TitleListEntry' },
						},
					};
				}

				return title;
			});

			this.$apollo.getClient().writeQuery<GetPopularTitlesQuery, GetPopularTitlesQueryVariables>({
				query: GetPopularTitlesDocument,
				variables: this.queryVariables,
				data: {
					popularTitles: {
						...titles.popularTitles,
						totalCount: titles?.popularTitles.totalCount,
						edges: updatedTitles,
					},
				},
			});
		}
	}

	onLoadMore({ event }: { event: any }) {
		event &&
			this.$apollo.queries.popularPageData
				.fetchMore({
					variables: {
						popularAfterCursor: this.popularPageData.pageInfo.endCursor,
					},
					updateQuery(previousResult, { fetchMoreResult }) {
						return {
							popularTitles: {
								__typename: previousResult.popularTitles.__typename,
								totalCount: fetchMoreResult.popularTitles.totalCount,
								edges: [...previousResult.popularTitles.edges, ...fetchMoreResult.popularTitles.edges],
								pageInfo: fetchMoreResult.popularTitles.pageInfo,
							},
						};
					},
				})
				.catch(e => {
					captureMessageForSentry(
						'[GraphQL Popular Page Load More Titles Issue]:',
						{ error: e, where: 'Component: Popular, method: onLoadMore' },
						Sentry.Severity.Error
					);
				})
				.finally(() => event.target && event.target.complete(this.popularTitles.length));
	}

	@Watch('filterPopular')
	changedFilters(newValue: any, oldValue: any) {
		if (!isEqual(newValue, oldValue)) {
			this.isFirstPageLoaded = false;
			this.scrollToTop();
		}
	}

	@Watch('isTitlesLoading')
	onIsFetchingTitles(isTitlesLoading: boolean) {
		if (isTitlesLoading) return;
		sentryPerformanceTracker.finishTransaction(PageNavigationTransaction.Popular);
	}

	@Watch('isLoggedIn')
	onIsLoggedInChange() {
		this.$apollo.queries.popularPageData.refetch();
	}

	trackClick(payload: TitleEventPayloadGraphql) {
		TitleHelper.trackTitleEventGraphql('title_clicked', payload, 'popular-timeline');
		providerPageExpTrackEvent({
			label: 'title_clicked_lower',
			action: 'click',
		});
	}

	showErrorToast() {
		ToastHelper.error({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}

	scrollToTop() {
		this.content.scrollToTop(0, 'auto');
	}
}
